import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  BottomSheet,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";

import { useCreateReviewUnlock } from "../api/useCreateReviewUnlock";

interface NoPriorWorkplacesBottomSheetProps {
  modalState: UseModalState;
  originatingWorkplaceId: string;
  onUnlocked: () => void;
}

export function NoPriorWorkplacesBottomSheet(props: NoPriorWorkplacesBottomSheetProps) {
  const { modalState, originatingWorkplaceId, onUnlocked } = props;
  const { showSuccessToast, showErrorToast } = useToast();
  const { mutateAsync: unlockReviews, isLoading: isUnlocking } = useCreateReviewUnlock();

  async function handleUnlock() {
    try {
      await unlockReviews();
    } catch {
      showErrorToast("Couldn't unlock reviews. Please try again.");
      return;
    }

    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_TEMPORARY_UNLOCK_COMPLETED, {
      originatingWorkplaceId,
    });
    showSuccessToast("Reviews unlocked for 6 months");
    modalState.closeModal();
    onUnlocked();
  }

  function handleClose() {
    modalState.closeModal();
  }

  return (
    <BottomSheet
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={isUnlocking}
            onClick={() => handleUnlock()}
          >
            Unlock reviews
          </Button>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            disabled={isUnlocking}
            onClick={() => handleClose()}
          >
            Cancel
          </Button>
        </DialogFooter>
      }
      header={<BottomSheetHeader onClose={() => handleClose()} />}
      modalState={modalState}
    >
      <DialogContent sx={{ backgroundColor: "unset", paddingY: 8, marginTop: 8 }}>
        <Stack direction="column" spacing={6}>
          <Title bold variant="h2" component="h2">
            No workplaces to review?
          </Title>
          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
            That&apos;s okay. We&apos;ll unlock reviews for 6 months. Workplace reviews help other
            workers make informed decisions — consider leaving one if you worked here.
          </Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
